import { Component, EventEmitter, Input, OnInit, Optional, Output, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Params, RouterOutlet } from '@angular/router';
import { IonContent, IonRouterOutlet, NavController, Platform } from '@ionic/angular';
import { BehaviorSubject, combineLatest, fromEvent, of, ReplaySubject, timer } from 'rxjs';
import { tap, takeUntil, map, filter, take, distinctUntilChanged } from 'rxjs/operators';

import { ScrollService } from '../scroll-service/scroll-service';
import { RouterService } from '../router.service';

import { ThemeService } from './theme.service';
import { DataLibrary } from '../data-library';

@Component({
    standalone: false,
    selector: 'abs-page-layout',
    template: `
        <div
            [class]="theme + (themeService.factionTheme() ? ' ' + themeService.factionTheme() : '') + ' themeWrap'"
            [class.desktop]="themeService.isDesktop()"
        >
            <ion-header>
                <ion-toolbar>
                    <ion-buttons slot="start">
                        <ion-back-button
                            [defaultHref]="defaultHref ?? '..'"
                            *ngIf="shouldShowBackButton$ | async"
                            (click)="navigateBack($event)"
                        ></ion-back-button>
                    </ion-buttons>
                    <ion-title>
                        <div class="wrap">
                            <div class="title" (click)="titleClicked.emit()">{{ pageTitle ?? title }}</div>
                            <div class="subtitle" *ngIf="subTitle">{{ subTitle }}</div>
                        </div>
                    </ion-title>
                    <ion-buttons slot="end">
                        <ion-menu-button></ion-menu-button>
                    </ion-buttons>
                </ion-toolbar>
            </ion-header>
            <!-- @if (themeService.isDesktop()) {
                <div class="fullPageContent">
                    <ng-container [ngTemplateOutlet]="innerContent"></ng-container>
                </div>
            } @else { -->
            <ion-content #content fullscreen="true">
                <ng-container [ngTemplateOutlet]="innerContent"></ng-container>
            </ion-content>
            <!-- } -->

            <ng-template #innerContent>
                <div class="pageWrap" *ngIf="ready$ | async">
                    <div class="pageContent">
                        <ng-content></ng-content>
                    </div>
                </div>
            </ng-template>
        </div>
    `,
    styles: [
        `
            :host {
                flex: 1 1 auto;
                display: flex;
                flex-direction: column;
                height: 100%; /* This fixes the admin analytics page layout */
            }

            :host > div {
                height: 100%;
            }

            .fullpageContent {
                overflow-y: auto;
                position: absolute;
                width: 100%;
                height: 100%;
            }

            @media screen and (max-width: 991px) {
                ion-title .wrap {
                    width: auto;
                    margin: 0;
                }
            }

            @media screen and (min-width: 992px) {
                ion-menu-button {
                    visibility: hidden;
                    pointer-events: none;
                }
            }
        `
    ]
})
export class PageLayoutComponent implements OnInit, AfterViewInit {
    @Input() pageTitle: string | undefined;
    /** @deprecated Use pageTitle instead */
    @Input() title: string | undefined;
    @Input() subTitle: string | null;
    @Input() defaultHref: string | null = null;
    @Input() set displayBackLink(value: boolean) {
        this._displayBackLink = value;
        this.displayBackLink$.next(value);
    }
    get displayBackLink(): boolean {
        return this._displayBackLink;
    }
    private _displayBackLink: boolean = true;
    private displayBackLink$ = new BehaviorSubject<boolean>(true);

    @Input() preserveQueryString: boolean = false;
    @ViewChild('content') content: IonContent;

    @Output() titleClicked = new EventEmitter();
    hasScrollParent = true;

    destroy$ = new ReplaySubject();
    resize$ = fromEvent(window, 'resize').pipe(takeUntil(this.destroy$));
    resizeSub = this.resize$.subscribe((e: any) => {
        this.width$.next(e.currentTarget.innerWidth);
    });

    width$ = new BehaviorSubject(window.innerWidth);

    routeData$ = this.routerService.routeData$;

    theme: string;

    ready$ = this.dataLibrary?.ready$ || of(true);

    // Track whether sidebar is visible based on THIS component's route data
    // Use the component's own ActivatedRoute instead of the global routeData$
    sidebarVisible$ = this.route.data.pipe(
        map((data) => {
            const outlet = this.route.outlet;
            const isPrimaryOutlet = !outlet || outlet === 'primary';
            // Only check for sidebar visibility if this is the primary outlet
            if (!isPrimaryOutlet) {
                return false;
            }
            // Check if current route has sidebar hidden
            const hideSidebar = data['hideSidebar'];
            return !(hideSidebar ?? true);
        })
    );

    // Determine whether to show back button based on displayBackLink input and sidebar visibility
    shouldShowBackButton$ = combineLatest([this.displayBackLink$, this.sidebarVisible$]).pipe(
        map(([displayBackLink, sidebarVisible]) => {
            const outlet = this.route.outlet;
            const isPrimaryOutlet = !outlet || outlet === 'primary';

            console.log('[PageLayout] Back button logic:', {
                displayBackLink,
                sidebarVisible,
                outlet,
                isPrimaryOutlet,
                isDesktop: this.themeService.isDesktop()
            });

            if (!displayBackLink) {
                console.log('[PageLayout] Hiding: displayBackLink is false');
                return false;
            }

            // In desktop mode, only hide the back button in the primary outlet if sidebar is visible
            if (this.themeService.isDesktop() && isPrimaryOutlet && sidebarVisible) {
                console.log('[PageLayout] Hiding: desktop + primary + sidebar visible');
                return false;
            }

            console.log('[PageLayout] Showing back button');
            return true;
        })
    );

    constructor(
        protected themeService: ThemeService,
        protected route: ActivatedRoute,
        protected scrollService: ScrollService,
        protected navCtrl: NavController,
        protected routerService: RouterService,
        protected platform: Platform,
        @Optional()
        protected dataLibrary: DataLibrary,
        @Optional()
        protected routerOutlet: IonRouterOutlet
    ) {
        this.scrollService.reset(route.outlet);
    }

    ngOnInit(): void {
        // Subscribe to route data changes and update theme
        // The distinctUntilChanged prevents unnecessary theme updates
        this.route.data
            .pipe(
                takeUntil(this.destroy$),
                map((d) => d['theme'] || d['gameId'] || 'home'),
                distinctUntilChanged()
            )
            .subscribe((theme) => {
                // Only set theme if this component is in the primary outlet
                // PDF and other standalone pages don't use abs-page-layout,
                // so we need something else to handle their theme
                const outlet = this.route.outlet;
                const isPrimaryOutlet = !outlet || outlet === 'primary';

                if (isPrimaryOutlet) {
                    console.log('[PageLayout] Setting theme to:', theme, 'outlet:', outlet);
                    this.themeService.selectTheme(theme);
                }
            });

        this.themeService.theme$
            .pipe(
                filter((t) => !!t),
                take(1)
            )
            .subscribe((t) => {
                this.theme = t;
            });
    }

    ngAfterViewInit() {
        this.initScroll();
        this.resize$.subscribe(() => {
            this.initScroll();
        });
    }

    initScroll() {
        if (this.hasScrollParent) {
            const cancelLoop$ = new ReplaySubject();
            timer(0, 200)
                .pipe(
                    tap((i) => {
                        if (i > 10) {
                            cancelLoop$.next(true);
                        }
                    }),
                    takeUntil(cancelLoop$),
                    takeUntil(this.scrollService.outlets[this.route.outlet].scrollEl$),
                    map(async () => await this.content),
                    filter((x) => !!x)
                )
                .subscribe(async () => {
                    if (!this.content) {
                        return;
                    }
                    this.content.scrollEvents = true;
                    this.scrollService.outlets[this.route.outlet].content$.next(await this.content);
                    this.scrollService.outlets[this.route.outlet].scrollEl$.next(await this.content.getScrollElement());

                    // Restore scroll position for sidebar outlet
                    if (this.route.outlet === 'sidebar') {
                        const savedPosition = this.scrollService.getScrollPosition('sidebar');
                        if (savedPosition !== undefined) {
                            setTimeout(() => {
                                this.content.scrollToPoint(0, savedPosition, 0);
                            }, 100);
                        }

                        // Save scroll position on scroll events
                        const scrollEl = await this.content.getScrollElement();
                        if (scrollEl) {
                            const subscription = fromEvent(scrollEl, 'scroll')
                                .pipe(takeUntil(this.destroy$))
                                .subscribe(() => {
                                    this.scrollService.saveScrollPosition('sidebar', scrollEl.scrollTop);
                                });
                        }
                    }
                });
        }
    }

    navigateBack(ev) {
        let qs: Params = {};
        if (this.preserveQueryString) {
            qs = this.route.snapshot.queryParams;
        }

        if (this.defaultHref) {
            const resolved = this.resolveHref(this.defaultHref);
            this.navCtrl.setDirection('back');
            this.navCtrl.navigateBack(resolved, { animationDirection: 'back', queryParams: qs });
            ev.preventDefault();
            return;
        }

        // if (!this.routerOutlet) {
        const startingPath = location.pathname;
        const urlParts = startingPath.split('/');
        this.navCtrl.setDirection('back');

        const tryParentRoute = () => {
            if (urlParts.length > 1) {
                urlParts.pop();
                const url = urlParts.join('/');

                console.log('Attempting to navigate to ' + url);
                return this.navCtrl
                    .navigateBack(url, { animationDirection: 'back', queryParams: qs })
                    .then(() => {
                        if (location.pathname === startingPath) {
                            return tryParentRoute();
                        } else {
                            ev.preventDefault();
                        }
                    })
                    .catch(() => {
                        return tryParentRoute();
                    });
            } else {
                console.log('No ancestor route found.  Navigating to root.');
                return this.navCtrl.navigateBack('/', { animationDirection: 'back' });
            }
        };

        return tryParentRoute();
        // }
    }

    private resolveHref(href: string): string {
        if (href.startsWith('/')) return href;
        const parts = location.pathname.split('/');
        for (const segment of href.split('/')) {
            if (segment === '..') {
                parts.pop();
            } else if (segment !== '.') {
                parts.push(segment);
            }
        }
        return parts.join('/');
    }
}
